import { useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import {
  listBranches,
  createBranch,
  updateBranch,
  deactivateBranch,
  updateAttendanceDefaults,
  listBoards,
  uploadCampusLogo,
  type BranchDoc,
  type BoardDoc,
} from "../../service/branches"
import { getCampusLogoFallbackUrl } from "../../utils/campusLogos"

const EMPTY: Partial<BranchDoc> = {
  name: "", code: "", campusNo: "", schoolName: "", schoolCode: "",
  address: "", contactNumber: "", email: "", principalName: "",
  logoKey: "", logoUrl: "", blackLogoKey: "", blackLogoUrl: "", strokeLogoKey: "", strokeLogoUrl: "", boards: [],
  studentAttendanceDefault: "present", staffAttendanceDefault: "present",
}

const formatPendingDate = (iso?: string | null) => {
  if (!iso) return ""
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) }
  catch { return "" }
}

type LogoSlot = {
  id: "main" | "black" | "stroke"
  label: string
  keyField: "logoKey" | "blackLogoKey" | "strokeLogoKey"
  urlField: "logoUrl" | "blackLogoUrl" | "strokeLogoUrl"
  fallbackSuffix: "" | "-black" | "-stroke"
  help: string
}

const LOGO_SLOTS: LogoSlot[] = [
  { id: "main", label: "Main Logo", keyField: "logoKey", urlField: "logoUrl", fallbackSuffix: "", help: "Default colored campus logo." },
  { id: "black", label: "Black Logo", keyField: "blackLogoKey", urlField: "blackLogoUrl", fallbackSuffix: "-black", help: "Single-color logo for letterheads and high-contrast print." },
  { id: "stroke", label: "Stroke Logo", keyField: "strokeLogoKey", urlField: "strokeLogoUrl", fallbackSuffix: "-stroke", help: "Outlined logo variant where available." },
]

const fallbackLogoUrl = (campusNo?: string, slot?: LogoSlot) => {
  if (!slot) return ""
  return getCampusLogoFallbackUrl(campusNo, slot.id)
}

const currentLogoUrl = (branch: Partial<BranchDoc>, slot: LogoSlot) =>
  (branch[slot.urlField] as string | null | undefined) || fallbackLogoUrl(branch.campusNo, slot)

type CropState = {
  slot: LogoSlot
  objectUrl: string
  fileName: string
  zoom: number
  offsetX: number
  offsetY: number
  naturalWidth: number
  naturalHeight: number
}

const boardIds = (b?: (string | BoardDoc)[]): string[] =>
  (b || []).map((x) => (typeof x === "object" ? x._id : x))

const CampusManagement = () => {
  const [campuses, setCampuses] = useState<BranchDoc[]>([])
  const [boards, setBoards] = useState<BoardDoc[]>([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState<null | "new" | "edit">(null)
  const [form, setForm] = useState<Partial<BranchDoc>>(EMPTY)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [originalDefaults, setOriginalDefaults] = useState<{
    studentAttendanceDefault?: "present" | "absent"
    staffAttendanceDefault?: "present" | "absent"
  }>({})
  const [saving, setSaving] = useState(false)
  const [uploadingSlot, setUploadingSlot] = useState<LogoSlot["id"] | null>(null)
  const [crop, setCrop] = useState<CropState | null>(null)
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const load = async () => {
    setLoading(true)
    try {
      const [c, b] = await Promise.all([listBranches(), listBoards().catch(() => [])])
      setCampuses(c); setBoards(b)
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load campuses")
    } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const openNew = () => { setForm(EMPTY); setEditingId(null); setModal("new") }
  const openEdit = (c: BranchDoc) => {
    setForm({ ...c, boards: boardIds(c.boards) }); setEditingId(c._id); setModal("edit")
    setOriginalDefaults({
      studentAttendanceDefault: c.studentAttendanceDefault || "absent",
      staffAttendanceDefault: c.staffAttendanceDefault || "absent",
    })
  }
  const set = (k: keyof BranchDoc, v: any) => setForm((f) => ({ ...f, [k]: v }))
  const toggleBoard = (id: string) =>
    setForm((f) => {
      const cur = boardIds(f.boards)
      return { ...f, boards: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] }
    })

  const onPickLogo = (slot: LogoSlot, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file")
      return
    }
    const objectUrl = URL.createObjectURL(file)
    setCrop({
      slot,
      objectUrl,
      fileName: file.name,
      zoom: 1,
      offsetX: 0,
      offsetY: 0,
      naturalWidth: 0,
      naturalHeight: 0,
    })
    e.target.value = ""
  }

  const closeCrop = () => {
    if (crop?.objectUrl) URL.revokeObjectURL(crop.objectUrl)
    setCrop(null)
  }

  const uploadCroppedLogo = async () => {
    if (!crop || !crop.naturalWidth || !crop.naturalHeight) return
    const frame = 320
    const output = 640
    const baseScale = Math.min(frame / crop.naturalWidth, frame / crop.naturalHeight)
    const renderedW = crop.naturalWidth * baseScale * crop.zoom
    const renderedH = crop.naturalHeight * baseScale * crop.zoom
    const left = (frame - renderedW) / 2 + crop.offsetX
    const top = (frame - renderedH) / 2 + crop.offsetY

    const sx = Math.max(0, (0 - left) / renderedW * crop.naturalWidth)
    const sy = Math.max(0, (0 - top) / renderedH * crop.naturalHeight)
    const sw = Math.min(crop.naturalWidth - sx, frame / renderedW * crop.naturalWidth)
    const sh = Math.min(crop.naturalHeight - sy, frame / renderedH * crop.naturalHeight)

    const img = new Image()
    img.src = crop.objectUrl
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error("Failed to load image"))
    })

    const canvas = document.createElement("canvas")
    canvas.width = output
    canvas.height = output
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.clearRect(0, 0, output, output)
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, output, output)

    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, "image/png"))
    if (!blob) {
      toast.error("Could not crop image")
      return
    }

    setUploadingSlot(crop.slot.id)
    try {
      const file = new File([blob], crop.fileName.replace(/\.[^.]+$/, "") + ".png", { type: "image/png" })
      const { key, url } = await uploadCampusLogo(file, crop.slot.id)
      const updated = { ...form, [crop.slot.keyField]: key, [crop.slot.urlField]: url }
      setForm(updated)

      // Persist immediately — otherwise the upload only lives in local form
      // state and is silently lost if the modal is closed without a
      // separate "Save" click, even though this toast already said success.
      if (editingId) {
        await updateBranch(editingId, updated)
        load()
      }

      toast.success(`${crop.slot.label} uploaded and saved`)
      closeCrop()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Upload failed")
    } finally {
      setUploadingSlot(null)
    }
  }

  const save = async () => {
    if (!form.name || !form.campusNo) { toast.error("Name and Campus No are required"); return }
    setSaving(true)
    try {
      if (modal === "edit" && editingId) {
        await updateBranch(editingId, form)
        const studentChanged = form.studentAttendanceDefault && form.studentAttendanceDefault !== originalDefaults.studentAttendanceDefault
        const staffChanged = form.staffAttendanceDefault && form.staffAttendanceDefault !== originalDefaults.staffAttendanceDefault
        if (studentChanged || staffChanged) {
          await updateAttendanceDefaults(editingId, {
            studentDefault: studentChanged ? form.studentAttendanceDefault : undefined,
            staffDefault: staffChanged ? form.staffAttendanceDefault : undefined,
          })
        }
        toast.success("Campus updated")
      } else {
        await createBranch(form)
        toast.success("Campus created")
      }
      setModal(null); load()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Save failed")
    } finally { setSaving(false) }
  }

  const deactivate = async (c: BranchDoc) => {
    if (!window.confirm(`Deactivate "${c.name}"? It will be hidden from the campus switcher (data is not deleted).`)) return
    try { await deactivateBranch(c._id); toast.success("Campus deactivated"); load() }
    catch (e: any) { toast.error(e?.response?.data?.message || "Failed to deactivate") }
  }

  const boardChips = (c: BranchDoc) => {
    const ids = boardIds(c.boards)
    const found = boards.filter((b) => ids.includes(b._id))
    if (!found.length) return <span className="text-muted">—</span>
    return found.map((b) => <span key={b._id} className="badge bg-primary-transparent text-primary me-1">{b.code}</span>)
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex align-items-center justify-content-between mb-3">
          <div>
            <h3 className="page-title mb-1">Campus Management</h3>
            <p className="text-muted mb-0">Add, edit, or deactivate campuses. Each campus has its own logo, boards, address, phone, and school name.</p>
          </div>
          <button className="btn btn-primary" onClick={openNew}>+ Add Campus</button>
        </div>

        <div className="card">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-bordered table-hover mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 50 }}>#</th>
                    <th style={{ width: 60 }}>Logo</th>
                    <th>Name</th>
                    <th>Code</th>
                    <th>School</th>
                    <th>Phone</th>
                    <th>Boards</th>
                    <th>Attendance Defaults</th>
                    <th>Status</th>
                    <th style={{ width: 160 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={10} className="text-center py-4 text-muted">Loading…</td></tr>
                  ) : campuses.length === 0 ? (
                    <tr><td colSpan={10} className="text-center py-4 text-muted">No campuses yet.</td></tr>
                  ) : (
                    campuses.map((c) => (
                      <tr key={c._id}>
                        <td>{c.campusNo}</td>
                        <td>
                          {currentLogoUrl(c, LOGO_SLOTS[0]) ? (
                            <img src={currentLogoUrl(c, LOGO_SLOTS[0])} alt={c.name} style={{ width: 34, height: 34, objectFit: "contain" }} />
                          ) : <span className="text-muted">—</span>}
                        </td>
                        <td className="fw-semibold">{c.name}</td>
                        <td>{c.code || "—"}</td>
                        <td className="small">{c.schoolName || "—"}{c.schoolCode ? <span className="badge bg-secondary-transparent ms-1">{c.schoolCode}</span> : null}</td>
                        <td className="small">{c.contactNumber || "—"}</td>
                        <td>{boardChips(c)}</td>
                        <td className="small">
                          <div>Student: <span className="fw-semibold text-capitalize">{c.studentAttendanceDefault || "absent"}</span>
                            {c.studentAttendanceDefaultPending && (
                              <span className="text-muted"> → {c.studentAttendanceDefaultPending} from {formatPendingDate(c.studentAttendanceDefaultEffectiveFrom)}</span>
                            )}
                          </div>
                          <div>Staff: <span className="fw-semibold text-capitalize">{c.staffAttendanceDefault || "absent"}</span>
                            {c.staffAttendanceDefaultPending && (
                              <span className="text-muted"> → {c.staffAttendanceDefaultPending} from {formatPendingDate(c.staffAttendanceDefaultEffectiveFrom)}</span>
                            )}
                          </div>
                        </td>
                        <td>{c.isActive === false ? <span className="badge bg-secondary">inactive</span> : <span className="badge bg-success">active</span>}</td>
                        <td>
                          <button className="btn btn-sm btn-outline-primary me-2" onClick={() => openEdit(c)}>Edit</button>
                          {c.isActive !== false && (
                            <button className="btn btn-sm btn-outline-danger" onClick={() => deactivate(c)}>Deactivate</button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {modal && (
          <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.5)" }} onClick={() => setModal(null)}>
            <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{modal === "edit" ? "Edit Campus" : "Add Campus"}</h5>
                  <button className="btn-close" onClick={() => setModal(null)} />
                </div>
                <div className="modal-body">
                  <div className="row g-3 mb-3">
                    {LOGO_SLOTS.map((slot) => {
                      const url = currentLogoUrl(form, slot)
                      const hasUploaded = Boolean(form[slot.keyField])
                      return (
                        <div className="col-md-4" key={slot.id}>
                          <div className="border rounded p-2 h-100">
                            <div className="d-flex align-items-center gap-2">
                              <div className="border rounded d-flex align-items-center justify-content-center" style={{ width: 72, height: 72, overflow: "hidden", background: "#f8f9fa", flexShrink: 0 }}>
                                {url ? (
                                  <img src={url} alt={slot.label} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                                ) : (
                                  <i className="ti ti-building text-muted" style={{ fontSize: 28 }} />
                                )}
                              </div>
                              <div className="min-w-0">
                                <label className="form-label mb-1">{slot.label}</label>
                                <div className="d-flex gap-1 flex-wrap">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => fileRefs.current[slot.id]?.click()}
                                    disabled={uploadingSlot === slot.id}
                                  >
                                    {uploadingSlot === slot.id ? "Uploading..." : hasUploaded ? "Replace" : "Upload"}
                                  </button>
                                  {hasUploaded && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => setForm((f) => ({ ...f, [slot.keyField]: "", [slot.urlField]: "" }))}
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                            <input
                              ref={(el) => { fileRefs.current[slot.id] = el }}
                              type="file"
                              accept="image/*"
                              className="d-none"
                              onChange={(e) => onPickLogo(slot, e)}
                            />
                            <small className="text-muted d-block mt-2">{slot.help}</small>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  <div className="row g-3">
                    <div className="col-md-8"><label className="form-label">Name *</label><input className="form-control" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
                    <div className="col-md-4"><label className="form-label">Campus No *</label><input className="form-control" value={form.campusNo || ""} onChange={(e) => set("campusNo", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label">Code</label><input className="form-control" value={form.code || ""} onChange={(e) => set("code", e.target.value)} placeholder="e.g. NSB" /></div>
                    <div className="col-md-6"><label className="form-label">Phone</label><input className="form-control" value={form.contactNumber || ""} onChange={(e) => set("contactNumber", e.target.value)} /></div>
                    <div className="col-md-8"><label className="form-label">School name (masthead)</label><input className="form-control" value={form.schoolName || ""} onChange={(e) => set("schoolName", e.target.value)} placeholder="e.g. Metropolitan Academy" /></div>
                    <div className="col-md-4"><label className="form-label">School code</label><input className="form-control" value={form.schoolCode || ""} onChange={(e) => set("schoolCode", e.target.value)} placeholder="MPA" /></div>
                    <div className="col-md-6"><label className="form-label">Email</label><input className="form-control" value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label">Principal</label><input className="form-control" value={form.principalName || ""} onChange={(e) => set("principalName", e.target.value)} /></div>
                    <div className="col-12"><label className="form-label">Address</label><textarea className="form-control" rows={2} value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
                    <div className="col-md-6">
                      <label className="form-label">Student Attendance Default</label>
                      <select
                        className="form-select"
                        value={form.studentAttendanceDefault || "present"}
                        onChange={(e) => set("studentAttendanceDefault", e.target.value as "present" | "absent")}
                      >
                        <option value="present">Present</option>
                        <option value="absent">Absent</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Staff Attendance Default</label>
                      <select
                        className="form-select"
                        value={form.staffAttendanceDefault || "present"}
                        onChange={(e) => set("staffAttendanceDefault", e.target.value as "present" | "absent")}
                      >
                        <option value="present">Present</option>
                        <option value="absent">Absent</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <small className="text-muted">
                        The default status shown for anyone not yet marked on the Attendance screen (student/staff each have their own setting). Changes apply from tomorrow — today's and past attendance are never affected.
                      </small>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Boards this campus operates</label>
                      <div className="d-flex flex-wrap gap-3">
                        {boards.length === 0 ? <span className="text-muted small">No boards defined yet.</span> : boards.map((b) => (
                          <div className="form-check" key={b._id}>
                            <input className="form-check-input" type="checkbox" id={`bd-${b._id}`} checked={boardIds(form.boards).includes(b._id)} onChange={() => toggleBoard(b._id)} />
                            <label className="form-check-label" htmlFor={`bd-${b._id}`}>{b.name} <span className="text-muted">({b.code})</span></label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setModal(null)}>Cancel</button>
                  <button className="btn btn-primary" onClick={save} disabled={saving || Boolean(uploadingSlot)}>{saving ? "Saving…" : "Save"}</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {crop && (
          <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.55)" }} onClick={closeCrop}>
            <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Crop {crop.slot.label}</h5>
                  <button className="btn-close" onClick={closeCrop} />
                </div>
                <div className="modal-body">
                  <div className="d-flex justify-content-center mb-3">
                    <div
                      style={{
                        width: 320,
                        height: 320,
                        overflow: "hidden",
                        position: "relative",
                        background: "#f1f5f9",
                        boxShadow: "0 0 0 1px #d0d7de inset",
                      }}
                    >
                      <img
                        src={crop.objectUrl}
                        alt="Crop preview"
                        onLoad={(e) => {
                          const img = e.currentTarget
                          setCrop((c) => c ? { ...c, naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight } : c)
                        }}
                        style={{
                          position: "absolute",
                          left: "50%",
                          top: "50%",
                          maxWidth: "100%",
                          maxHeight: "100%",
                          transform: `translate(calc(-50% + ${crop.offsetX}px), calc(-50% + ${crop.offsetY}px)) scale(${crop.zoom})`,
                          transformOrigin: "center center",
                          userSelect: "none",
                          pointerEvents: "none",
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          border: "2px solid rgba(37,99,235,.9)",
                          boxShadow: "0 0 0 999px rgba(15,23,42,.08)",
                          pointerEvents: "none",
                        }}
                      />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="form-label mb-1">Zoom</label>
                    <input
                      type="range"
                      className="form-range"
                      min={1}
                      max={4}
                      step={0.05}
                      value={crop.zoom}
                      onChange={(e) => setCrop((c) => c ? { ...c, zoom: Number(e.target.value) } : c)}
                    />
                  </div>
                  <div className="row g-2">
                    <div className="col-6">
                      <label className="form-label mb-1">Horizontal</label>
                      <input
                        type="range"
                        className="form-range"
                        min={-160}
                        max={160}
                        step={1}
                        value={crop.offsetX}
                        onChange={(e) => setCrop((c) => c ? { ...c, offsetX: Number(e.target.value) } : c)}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label mb-1">Vertical</label>
                      <input
                        type="range"
                        className="form-range"
                        min={-160}
                        max={160}
                        step={1}
                        value={crop.offsetY}
                        onChange={(e) => setCrop((c) => c ? { ...c, offsetY: Number(e.target.value) } : c)}
                      />
                    </div>
                  </div>
                  <small className="text-muted">The visible square will be saved as the uploaded logo.</small>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={closeCrop}>Cancel</button>
                  <button className="btn btn-primary" onClick={uploadCroppedLogo} disabled={uploadingSlot === crop.slot.id || !crop.naturalWidth}>
                    {uploadingSlot === crop.slot.id ? "Uploading..." : "Crop & Upload"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CampusManagement
